import React, { useEffect, useState, useMemo } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebase-config";
import ContractTipCard from "./ContractTipCard";
import Navbar from "./Navbar";
import Footer from "./Footer";
import "../Styles/ContractTips.css";
import SEO from "../components/SEO";

const SkeletonCard = () => (
  <div className="tip-card tip-skeleton" aria-hidden="true">
    <div className="skeleton-img" />
    <div className="tip-content">
      <div className="skeleton-badge" />
      <div className="skeleton-line lg" />
      <div className="skeleton-line md" />
      <div className="skeleton-line sm" />
      <div className="skeleton-line xs" />
    </div>
  </div>
);

const ContractTips = () => {
  const [tips, setTips]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [activeCategory, setActiveCategory] = useState("ALL");
  const [searchQuery, setSearchQuery]       = useState("");

  useEffect(() => {
    const fetchTips = async () => {
      try {
        const snapshot = await getDocs(collection(db, "contract_tips"));
        const all = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(t => t.draft !== true);
        setTips(all.sort((a, b) => (a.importance ?? 999) - (b.importance ?? 999)));
      } catch (err) {
        console.error("Error fetching contract tips:", err);
        setError("Couldn't load tips — please refresh.");
      } finally {
        setLoading(false);
      }
    };
    fetchTips();
  }, []);

  /* Dynamically build category list from Firebase data */
  const categories = useMemo(() => {
    const cats = new Set(
      tips.map(t => t.category?.toUpperCase()).filter(Boolean)
    );
    return ["ALL", ...Array.from(cats)];
  }, [tips]);

  /* Filter by active category + search query */
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return tips.filter(tip => {
      const catMatch =
        activeCategory === "ALL" ||
        tip.category?.toUpperCase() === activeCategory;
      const textMatch =
        !q ||
        tip.title?.toLowerCase().includes(q) ||
        tip.summary?.toLowerCase().includes(q) ||
        tip.category?.toLowerCase().includes(q) ||
        tip.tags?.some(t => t.toLowerCase().includes(q));
      return catMatch && textMatch;
    });
  }, [tips, activeCategory, searchQuery]);

  const featured = filtered[0] ?? null;
  const rest     = filtered.slice(1);

  const clearFilters = () => {
    setSearchQuery("");
    setActiveCategory("ALL");
  };

  const tipsListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Contract Management Tips",
    description: "Practical contract tips for freelancers, consultants and agencies — NDAs, service agreements, payment terms, IP and more.",
    url: "https://pp-studio-website.web.app/contract-tips",
    numberOfItems: tips.length || undefined,
    itemListElement: tips.slice(0, 10).map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.title,
      url: `https://pp-studio-website.web.app/contract-tips/${t.id}`,
    })),
  };

  return (
    <>
      <SEO
        title="Contract Tips for Freelancers & Businesses"
        description="Practical contract tips on NDAs, service agreements, payment terms, intellectual property and more. Free expert guidance from a UK contract specialist."
        keywords="contract tips UK, NDA tips freelancers, service agreement advice, payment terms contracts, intellectual property contracts, freelance legal tips, contract management advice, commercial contracts guide"
        canonical="/contract-tips"
        schema={tipsListSchema}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contract Tips", path: "/contract-tips" }]}
      />
      <Navbar />

      <section className="contract-tips-page">

        {/* ── HEADER ── */}
        <div className="tips-header">
          <span className="tips-eyebrow">✦ CONTRACT LIBRARY ✦</span>
          <h1>Contract Management Tips</h1>
          <p>Smart, field-tested advice for freelancers, consultants, and agencies.</p>

          <div className="tips-search-wrap">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="tips-search"
              type="search"
              placeholder="Search tips, topics, tags…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              aria-label="Search contract tips"
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery("")} aria-label="Clear search">✕</button>
            )}
          </div>
        </div>

        {/* ── CATEGORY FILTERS (built from Firebase data) ── */}
        {!loading && categories.length > 1 && (
          <div className="tips-filter-row" role="tablist" aria-label="Filter by category">
            {categories.map(cat => (
              <button
                key={cat}
                role="tab"
                aria-selected={activeCategory === cat}
                className={`tips-filter-btn${activeCategory === cat ? " active" : ""}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
                {cat !== "ALL" && (
                  <span className="filter-count">
                    {tips.filter(t => t.category?.toUpperCase() === cat).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* ── SKELETON LOADING ── */}
        {loading && (
          <>
            <div className="tips-featured-skeleton">
              <SkeletonCard />
            </div>
            <div className="tips-grid">
              <SkeletonCard /><SkeletonCard /><SkeletonCard />
            </div>
          </>
        )}

        {/* ── ERROR STATE ── */}
        {error && (
          <div className="tips-error">
            <span>Error</span>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Try again</button>
          </div>
        )}

        {/* ── RESULTS COUNT ── */}
        {!loading && !error && (
          <p className="tips-result-count">
            <strong>{filtered.length}</strong> tip{filtered.length !== 1 ? "s" : ""}
            {activeCategory !== "ALL" && <> in <em>{activeCategory}</em></>}
            {searchQuery && <> matching "<em>{searchQuery}</em>"</>}
          </p>
        )}

        {/* ── FEATURED TIP ── */}
        {!loading && !error && featured && (
          <div className="tips-featured">
            <ContractTipCard tip={featured} featured />
          </div>
        )}

        {/* ── TIPS GRID ── */}
        {!loading && !error && rest.length > 0 && (
          <div className="tips-grid">
            {rest.map(tip => (
              <ContractTipCard key={tip.id} tip={tip} />
            ))}
          </div>
        )}

        {/* ── EMPTY STATE ── */}
        {!loading && !error && filtered.length === 0 && (
          <div className="tips-empty">
            <span className="empty-icon">—</span>
            <h3>No tips found</h3>
            <p>
              {searchQuery
                ? `No results for "${searchQuery}". Try a different keyword.`
                : `No tips in the ${activeCategory} category yet.`}
            </p>
            <button className="empty-cta" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}

      </section>

      <Footer />
    </>
  );
};

export default ContractTips;
